/**
 * Desktop-client download buttons for macOS / Windows / Linux.
 *
 * The XOP Command Center desktop app is the way staff are meant to use
 * the dashboard (background notifications, launch-on-login, tray). These
 * links hit the dashboard's public, signed installer feed
 * (`/api/desktop/download/<os>` — `routes/desktopUpdates.ts`), which
 * 302-redirects to the newest installer for that platform. The links are
 * same-origin on web and absolute on the desktop shell (via `env.apiUrl`),
 * and need no auth (the feed is public; the Tauri client verifies each
 * update's minisign signature).
 *
 * The OS the visitor is on is detected + offered as the primary button;
 * the other two are secondary so anyone can grab the build they need.
 */
import { Box, Button, Stack, Typography } from "@mui/material";

import { env } from "../lib/env.js";

type Os = "mac" | "windows" | "linux";

const LABELS: Record<Os, string> = {
  mac: "macOS",
  windows: "Windows",
  linux: "Linux",
};

function detectOs(): Os | null {
  if (typeof navigator === "undefined") return null;
  const ua = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (ua.includes("mac")) return "mac";
  if (ua.includes("win")) return "windows";
  if (ua.includes("linux") || ua.includes("x11")) return "linux";
  return null;
}

function downloadUrl(os: Os): string {
  return `${env.apiUrl}/api/desktop/download/${os}`;
}

export function DesktopDownloads({
  compact = false,
}: {
  /** Compact = a single row of equal buttons (no detected-OS hero). */
  compact?: boolean;
}): JSX.Element {
  const detected = detectOs();
  const order: Os[] = detected
    ? [detected, ...(["mac", "windows", "linux"] as Os[]).filter((o) => o !== detected)]
    : (["mac", "windows", "linux"] as Os[]);

  if (compact) {
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {order.map((os, i) => (
          <Button
            key={os}
            component="a"
            href={downloadUrl(os)}
            variant={i === 0 ? "contained" : "outlined"}
            disableElevation
            size="small"
          >
            {LABELS[os]}
          </Button>
        ))}
      </Stack>
    );
  }

  const [primary, ...rest] = order;
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Button
        component="a"
        href={downloadUrl(primary!)}
        variant="contained"
        disableElevation
        size="large"
        fullWidth
      >
        Download for {LABELS[primary!]}
      </Button>
      <Stack direction="row" spacing={1} justifyContent="center">
        {rest.map((os) => (
          <Button key={os} component="a" href={downloadUrl(os)} variant="text" size="small">
            {LABELS[os]}
          </Button>
        ))}
      </Stack>
      <Typography sx={{ fontSize: 12, color: "text.disabled", textAlign: "center" }}>
        XOP Command Center is the dashboard, native on your machine — background
        notifications and launch-on-login.
      </Typography>
    </Box>
  );
}
